import axios from 'axios';
import type { AxiosError } from 'axios';
import {
  API_ERROR_EMPTY_JSON_OBJECT,
  API_ERROR_MESSAGE_LIST_SEPARATOR,
  API_ERROR_STATUS_CODE_PREFIX,
  API_ERROR_STATUS_CODE_SUFFIX,
} from 'src/constants/apiError';
import { UNHANDLED_ERROR } from 'src/constants/errors';
import { ApiErrorDetails } from 'src/types/apiError';
import { UpgradeOffer } from 'src/types/upgradeOffer';

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

function getFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return undefined;
}

function normalizeMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmedMessage = value.trim();

    return trimmedMessage.length > 0 ? trimmedMessage : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const normalizedItems = value
      .map((item) => normalizeMessage(item))
      .filter((item): item is string => typeof item === 'string');

    return normalizedItems.length > 0
      ? normalizedItems.join(API_ERROR_MESSAGE_LIST_SEPARATOR)
      : undefined;
  }

  if (isRecord(value)) {
    const messageCandidate = normalizeMessage(value.message);

    if (messageCandidate) {
      return messageCandidate;
    }

    const detailCandidate = normalizeMessage(value.detail);

    if (detailCandidate) {
      return detailCandidate;
    }

    const errorCandidate = normalizeMessage(value.error);

    if (errorCandidate) {
      return errorCandidate;
    }

    const msgCandidate = normalizeMessage(value.msg);

    if (msgCandidate) {
      return msgCandidate;
    }

    try {
      const stringifiedMessage = JSON.stringify(value);

      return stringifiedMessage === API_ERROR_EMPTY_JSON_OBJECT
        ? undefined
        : stringifiedMessage;
    } catch {
      return undefined;
    }
  }

  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function extractStatusCodeFromData(data: unknown): number | undefined {
  if (!isRecord(data)) {
    return undefined;
  }

  const statusCodeCandidate = getFiniteNumber(data.statusCode);

  if (statusCodeCandidate !== undefined) {
    return statusCodeCandidate;
  }

  return getFiniteNumber(data.status);
}

function extractErrorCodeFromData(data: unknown): string | number | undefined {
  if (!isRecord(data)) {
    return undefined;
  }

  const errorCodeCandidate = data.errorCode;

  if (
    typeof errorCodeCandidate === 'string' ||
    typeof errorCodeCandidate === 'number'
  ) {
    return errorCodeCandidate;
  }

  const codeCandidate = data.code;

  if (typeof codeCandidate === 'string' || typeof codeCandidate === 'number') {
    return codeCandidate;
  }

  if (isRecord(data.error)) {
    const nestedCodeCandidate = data.error.code;

    if (
      typeof nestedCodeCandidate === 'string' ||
      typeof nestedCodeCandidate === 'number'
    ) {
      return nestedCodeCandidate;
    }
  }

  return undefined;
}

function extractUpgradeOfferFromData(data: unknown): UpgradeOffer | undefined {
  if (!isRecord(data) || !isRecord(data.upgradeOffer)) {
    return undefined;
  }

  const { code, discountPercent, dashboardUpgradeUrl, setupCallUrl } =
    data.upgradeOffer;
  const parsedDiscountPercent =
    typeof discountPercent === 'number' && Number.isFinite(discountPercent)
      ? discountPercent
      : undefined;

  if (
    typeof code !== 'string' ||
    typeof dashboardUpgradeUrl !== 'string' ||
    parsedDiscountPercent === undefined
  ) {
    return undefined;
  }

  return {
    code,
    discountPercent: parsedDiscountPercent,
    dashboardUpgradeUrl,
    setupCallUrl: typeof setupCallUrl === 'string' ? setupCallUrl : undefined,
  };
}

function extractMessageFromData(data: unknown): string | undefined {
  if (typeof data === 'string') {
    return normalizeMessage(data);
  }

  if (!isRecord(data)) {
    return normalizeMessage(data);
  }

  return (
    normalizeMessage(data.message) ??
    normalizeMessage(data.detail) ??
    normalizeMessage(data.error) ??
    normalizeMessage(data.errors) ??
    normalizeMessage(data.msg) ??
    normalizeMessage(data)
  );
}

function isAxiosError(error: unknown): error is AxiosError {
  if (typeof axios.isAxiosError === 'function') {
    return axios.isAxiosError(error);
  }

  return isRecord(error) && error.isAxiosError === true;
}

export function getApiErrorDetails(
  error: unknown,
  fallbackMessage: string = UNHANDLED_ERROR,
): ApiErrorDetails {
  if (isAxiosError(error)) {
    const statusCodeFromData = extractStatusCodeFromData(error.response?.data);
    const statusCode = statusCodeFromData ?? error.response?.status;
    const messageFromData = extractMessageFromData(error.response?.data);
    const message =
      messageFromData ?? normalizeMessage(error.message) ?? fallbackMessage;

    return {
      message,
      statusCode,
      errorCode: extractErrorCodeFromData(error.response?.data),
      upgradeOffer: extractUpgradeOfferFromData(error.response?.data),
    };
  }

  if (error instanceof Error) {
    const message = normalizeMessage(error.message) ?? fallbackMessage;

    return { message };
  }

  return { message: normalizeMessage(error) ?? fallbackMessage };
}

export function formatApiErrorMessage(
  message: string,
  statusCode?: number,
): string {
  if (typeof statusCode !== 'number') {
    return message;
  }

  return `${API_ERROR_STATUS_CODE_PREFIX}${statusCode}${API_ERROR_STATUS_CODE_SUFFIX}${message}`;
}
